var DataTransformer        = require("./DataTransformer"),
    PageController         = require("app/core/PageController"),
    PageView               = require("./PageView"),
    SeriesNotFoundPageView = require("./SeriesNotFoundPageView"),
    SeriesRepository       = require("app/Domain/Ott/Api/V2/DataAccess/Series/Repository"),
    deepEqual              = require("fast-deep-equal"),
    get                    = require("object-path").get,
    kind                   = require("enyo/kind"),
    logger                 = require("enyo/logger"),
    quickMacroize          = require("enyo/macroize").quickMacroize;

module.exports = kind(/** @lends  app.SeriesPage.PageController.prototype */ {

    /**
     * @private
     */
    name: "app.SeriesPage.PageController",

    /**
     * @private
     */
    kind: PageController,

    /**
     * Implement the logic for loading and displaying the page here.
     *
     * @param  {Object}    activation           The activation object
     * @param  {Object}    activation.params    Parameters to tell us what to do
     * @param  {boolean}   activation.finished  Flag if activation is finished
     * @param  {function}  activation.resolve   A promise resolver to fire when
     *                                          the page is active.
     * @param  {function}  activation.reject    A promise rejector to fire when
     *                                          the page failed to activate.
     * @public
     */
    execute: function(activation) {
        var app             = this.app,
            $appView        = app.view,
            $router         = app.$.router,
            defaultLanguage = app.$.i18nService.getDefaultLanguage(),
            language        = get(activation, "params.language", null) || defaultLanguage,
            seriesSlug      = get(activation, "params.seriesSlug", null) || "",
            seasonNr        = get(activation, "params.seasonNr", null),
            skipCache       = get(activation, "params.skipCache", false);

        // Loading graphic
        $appView.requestShowLoadingGraphic();

        // Load Series
        SeriesRepository.getBySlug(skipCache, language, seriesSlug)

            // Show View
            .then(this.bindSafely(function(seriesResponse) {
                var seriesData,
                    selectedSeasonNr;

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                if (seriesResponse.status === "ok") {
                    seriesData       = DataTransformer.transformSeries(language, seriesResponse.data, seasonNr);
                    this.updateState(activation.params, seriesData);
                    selectedSeasonNr = get(this.state, "seasonData.serial_nr", null);

                    // Redirect to different season as needed.
                    if (selectedSeasonNr && selectedSeasonNr !== seasonNr) {
                        activation.resolve(1, function() {
                            $router.redirect(quickMacroize("/{$language}/series/{$seriesSlug}/season/{$seasonNr}", {
                                language:   language,
                                seriesSlug: seriesSlug,
                                seasonNr:   selectedSeasonNr
                            }));
                        });

                        return;
                    }

                    // Show not found error if no seasons exist for this series.
                    else if (!get(this.state, "data.children.length", 0)) {
                        this.updateView(SeriesNotFoundPageView);
                        this.updateDocumentTitle();
                    }

                    // Everything's fine, lets update the view
                    else {
                        this.updateView(PageView);
                        this.updateDocumentTitle();
                    }
                }

                // Data wasn't found
                else {
                    this.updateState(activation.params);

                    switch (seriesResponse.type) {
                        case "not_found_error":
                            this.updateView(SeriesNotFoundPageView);
                            this.updateDocumentTitle();
                            break;
                        case "server_error":
                        case "server_timeout_error":
                        case "bad_request_error":
                        default:
                            activation.resolve(1, function() {
                                $router.redirect(quickMacroize("/{$language}/oops", {
                                    language: language
                                }));
                            });

                            return;
                    }
                }
                activation.resolve(1);
            }))

            // Oops
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                // The activation was canceled, we can bail early.
                if (activation.finished) {
                    return;
                }

                activation.resolve(1, function() {
                    $router.redirect(quickMacroize("/{$language}/oops", {
                        language: language
                    }));
                });
            }))

            // No more loading graphic
            .finally(function() {
                $appView.requestHideLoadingGraphic();
            });
    },

    /**
     * @param  {Object}   params      The params passed to this controller
     * @param  {Object=}  seriesData  Freshly loaded series data
     * @private
     */
    updateState: function(params, seriesData) {
        var state        = {},
            seasons      = get(seriesData, "children", []),
            serialLayout = get(seriesData, "serial_layout", null);

        // Build state
        state.params = params;
        state.data   = seriesData || null;

        // Series layouts which require selection of a particular season
        if (serialLayout === "grid_dropdown"
            || serialLayout === "carousel_dropdown"
            || !serialLayout) {

            // Find requested season
            if (params.seasonNr) {
            // Find requested season
                state.seasonData = seasons.find(function(season) {
                    return season.serial_nr === params.seasonNr;
                });
            }

            // No requested season found, pick first available season instead.
            if (!state.seasonData) {
                state.seasonData = seasons[0] || null;
            }
        }

        // Update state only if it actually changed
        if (!deepEqual(this.state, state)) {
            this.set("state", state);
        }

        // Broadcast state changes to app state
        this.app.$.state.set("pageMediaType", get(seriesData, "type", null));
        this.app.$.state.set("pageMediaSlug", get(seriesData, "meta.slug", null));
        this.app.$.state.set("pageMediaId", get(seriesData, "id", null));
    },

    /**
     * Update the document title.
     *
     * @private
     */
    updateDocumentTitle: function() {
        var i18nService = this.app.$.i18nService,
            state       = this.state || {},
            title       = get(state, "data.meta.title", null),
            season      = get(state, "seasonData.meta.title", null);

        if (this.app.get("documentTitleOwner") !== this) {
            return;
        }

        if (title && season) {
            this.app.setDocumentTitle(i18nService.getString("app.SeriesPage.Document.Title.title", {
                title:  String(title || ""),
                season: String(season || "")
            }));
        }
        else if (title) {
            this.app.setDocumentTitle(i18nService.getString("app.SeriesPage.Document.Title.noSeason", {
                title: String(title || "")
            }));
        }
        else {
            this.app.setDocumentTitle(i18nService.getString("app.SeriesPage.Document.Title.noTitle"));
        }
    }

});
